<template>
  <span class="relative inline-flex" @mouseenter="onHover" @mouseleave="hovered = false">
    <button
      type="button"
      class="relative inline-flex cursor-pointer rounded-full text-label transition-colors before:absolute before:-inset-[11px] before:content-[''] hover:text-heading focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-blue-bright"
      :aria-label="`Uitleg over ${subject}`"
      :aria-expanded="open"
      :aria-describedby="open ? tooltipId : undefined"
      @click="dismissed = false"
      @focus="onFocus"
      @blur="focused = false"
      @keydown.escape="dismissed = true"
    >
      <InfoIcon class="size-[22px]" aria-hidden="true" />
    </button>
    <span
      v-if="open"
      :id="tooltipId"
      ref="tooltip"
      role="tooltip"
      class="absolute bottom-full left-1/2 z-10 mb-2 w-64 max-w-[calc(100vw-24px)] -translate-x-1/2 rounded-lg border border-line bg-white p-3 text-[14px] leading-5 font-medium text-body shadow-lg"
      :style="{ marginLeft: `${shift}px` }"
    >
      {{ text }}
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useId, useTemplateRef, watch } from 'vue'

import InfoIcon from '@/assets/svg/info.svg?component'

defineProps<{
  /** What the explanation is about, read to screen readers as "Uitleg over ...". */
  subject: string
  text: string
}>()

/**
 * Hover and focus each hold the tooltip open on their own, so a pointer passing by cannot close
 * what the keyboard opened (WCAG 1.4.13). Escape dismisses it even while the trigger stays active;
 * a fresh hover, focus or tap ends the dismissal. A tap fires focus and click in one go, which is
 * why neither toggles: a toggle would close the tooltip again instantly.
 *
 * The 44px touch target is a pseudo element around the button instead of padding, so the focus
 * outline hugs the icon rather than the whole target.
 */
const hovered = ref(false)
const focused = ref(false)
const dismissed = ref(false)

const open = computed(() => (hovered.value || focused.value) && !dismissed.value)

function onHover() {
  hovered.value = true
  dismissed.value = false
}

function onFocus() {
  focused.value = true
  dismissed.value = false
}

const tooltipId = useId()
const tooltip = useTemplateRef('tooltip')

// Centered on the icon the tooltip can poke past the viewport on small screens, which hands the
// page a horizontal scrollbar. Measure whenever it opens or the viewport changes, and nudge it
// back inside.
const shift = ref(0)

async function reposition() {
  shift.value = 0

  if (!open.value) {
    return
  }

  // The measurement needs the tooltip rendered at its unshifted spot first.
  await nextTick()
  const rect = tooltip.value?.getBoundingClientRect()

  if (!rect) {
    return
  }

  const margin = 12
  const overflowRight = rect.right - (window.innerWidth - margin)
  const overflowLeft = margin - rect.left

  if (overflowRight > 0) {
    shift.value = -overflowRight
  } else if (overflowLeft > 0) {
    shift.value = overflowLeft
  }
}

watch(open, (value) => {
  // Listening only while open: every info button on the form would otherwise measure on resize.
  if (value) {
    window.addEventListener('resize', reposition)
  } else {
    window.removeEventListener('resize', reposition)
  }

  reposition()
})

onBeforeUnmount(() => window.removeEventListener('resize', reposition))
</script>
